import type { DrawingClientEvent, Point, ToolType } from "../../../shared/types";
import { CANVAS_WIDTH, CANVAS_HEIGHT } from "../../../shared/palette";
import { ONE_LINE_MAN_CLEAR_ID_PREFIX } from "../../../shared/canvasReducer";
import type { CanvasRenderer } from "./CanvasRenderer";

export type ActiveTool = "pen" | "line" | "eraser" | "bucket";

/** "Tremble" - higher levels shake wider (distance, in canvas logical units -
 * see CANVAS_WIDTH/CANVAS_HEIGHT, 1100x750) and faster (intervalMs, only
 * relevant to the Line tool's continuous preview-jitter - lower is faster).
 * Indexed directly by level 0-3 (index 0 is an unused placeholder - every call
 * site is already guarded by `trembleLevel > 0`, this just keeps the array's
 * index type aligned with the `0 | 1 | 2 | 3` field instead of needing a cast
 * at each lookup). */
const TREMBLE_CONFIG: readonly { distance: number; intervalMs: number }[] = [
  { distance: 0, intervalMs: 0 },
  { distance: 10, intervalMs: 220 },
  { distance: 18, intervalMs: 105 },
  { distance: 28, intervalMs: 50 },
];

interface SafeZone {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

function makeSafeZone(marginFraction: number): SafeZone {
  const marginX = CANVAS_WIDTH * marginFraction;
  const marginY = CANVAS_HEIGHT * marginFraction;
  return { minX: marginX, maxX: CANVAS_WIDTH - marginX, minY: marginY, maxY: CANVAS_HEIGHT - marginY };
}

/** "Claustrophobia" - the outer marginFraction of the canvas (independently on
 * each axis) can't be drawn on at all. Indexed directly by level 0-2 (index 0 is
 * an unused placeholder, same convention as TREMBLE_CONFIG above - every call
 * site is already guarded by `claustrophobiaLevel > 0`). */
const CLAUSTROPHOBIA_CONFIG: readonly SafeZone[] = [0, 0.15, 0.25].map(makeSafeZone);

function isInSafeZone(point: Point, zone: SafeZone): boolean {
  return point.x >= zone.minX && point.x <= zone.maxX && point.y >= zone.minY && point.y <= zone.maxY;
}

/** Liang-Barsky segment clip: returns the portion of segment a-b that lies
 * inside `zone`, or null if the whole segment misses it entirely. Used by
 * "Claustrophobia" so a drawn line never enters the forbidden border, however
 * its (possibly Tremble-jittered) endpoints land. */
function clipSegmentToZone(a: Point, b: Point, zone: SafeZone): { a: Point; b: Point } | null {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const p = [-dx, dx, -dy, dy];
  const q = [a.x - zone.minX, zone.maxX - a.x, a.y - zone.minY, zone.maxY - a.y];
  let t0 = 0;
  let t1 = 1;
  for (let i = 0; i < 4; i++) {
    if (p[i] === 0) {
      if (q[i]! < 0) return null;
      continue;
    }
    const r = q[i]! / p[i]!;
    if (p[i]! < 0) {
      if (r > t1) return null;
      if (r > t0) t0 = r;
    } else {
      if (r < t0) return null;
      if (r < t1) t1 = r;
    }
  }
  return {
    a: { x: a.x + t0 * dx, y: a.y + t0 * dy },
    b: { x: a.x + t1 * dx, y: a.y + t1 * dy },
  };
}

/** Nudges `point` by a random offset up to `distance` in a random direction,
 * clamped back into the canvas bounds exactly like `toCanvasPoint` already
 * clamps raw input - a jittered point must stay valid or the server's own
 * `isValidPoint` check would silently drop the whole event. */
function jitterPoint(point: Point, distance: number): Point {
  const angle = Math.random() * Math.PI * 2;
  const radius = Math.random() * distance;
  return {
    x: Math.max(0, Math.min(CANVAS_WIDTH, point.x + Math.cos(angle) * radius)),
    y: Math.max(0, Math.min(CANVAS_HEIGHT, point.y + Math.sin(angle) * radius)),
  };
}

/** "Confusion" - mirrors `point` around `anchor` (the true, untransformed position
 * where the current stroke/line started) so moving away from the anchor draws
 * the opposite way instead. Level 1 only flips the X axis (left/right); level 2
 * flips both axes. A pure function of the fixed anchor and the current raw
 * point is enough: negation is linear, so mirroring around the original anchor
 * produces exactly the same path as accumulating inverted deltas step by step
 * would, without needing any extra per-stroke state beyond the anchor that's
 * already tracked for other purposes. */
function invertPoint(anchor: Point, point: Point, level: 1 | 2): Point {
  const invertedX = anchor.x - (point.x - anchor.x);
  const invertedY = level >= 2 ? anchor.y - (point.y - anchor.y) : point.y;
  return {
    x: Math.max(0, Math.min(CANVAS_WIDTH, invertedX)),
    y: Math.max(0, Math.min(CANVAS_HEIGHT, invertedY)),
  };
}

const ID_ALPHABET = "0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ";
const ID_LENGTH = 12;

// These IDs only need to be unique among one artist's own drawing commands within
// a single turn - no security exposure - so a short random string does the job
// that a 36-character UUID used to, and every strokeStart/strokeEnd/fill (and
// each batch of stroke points) carries it over the wire. crypto.getRandomValues
// works everywhere, including the plain http://<lan-ip>:port URLs this app is
// usually reached over (unlike crypto.randomUUID, which needs a secure context);
// Math.random is only the last-resort fallback.
function randomId(): string {
  let id = "";
  if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
    const bytes = crypto.getRandomValues(new Uint8Array(ID_LENGTH));
    for (const b of bytes) id += ID_ALPHABET[b % ID_ALPHABET.length];
    return id;
  }
  for (let i = 0; i < ID_LENGTH; i++) id += ID_ALPHABET[Math.floor(Math.random() * ID_ALPHABET.length)];
  return id;
}

/** Canvas coordinates are kept to one decimal place - far below anything
 * visible at the 1100x750 logical resolution, but it trims each coordinate on
 * the wire from ~15 characters to ~5. Rounded before the artist's own local
 * apply too, so the artist, the server, and every viewer hold identical data. */
function roundCoord(value: number): number {
  return Math.round(value * 10) / 10;
}

function roundEventPoints(event: DrawingClientEvent): DrawingClientEvent {
  if (event.type === "strokeStart" || event.type === "strokePoint" || event.type === "fill") {
    return { ...event, point: { x: roundCoord(event.point.x), y: roundCoord(event.point.y) } };
  }
  return event;
}

function distance(a: Point, b: Point): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/** Captures the artist's input and turns it into structured DrawingClientEvents. */
export class CanvasEditor {
  private tool: ActiveTool = "pen";
  private color = "#000000";
  private brushSize = 8;
  private currentStrokeId: string | null = null;
  private lineAnchor: Point | null = null;
  private lastPoint: Point | null = null;
  private enabled = false;
  /** Tracks whether the pointer button is physically held, independent of
   * `currentStrokeId` - needed because "Claustrophobia" can leave a pen stroke
   * closed (currentStrokeId null) while the artist is still mid-drag inside the
   * forbidden border, and handlePointerMove needs to tell that apart from just
   * hovering with no button down. */
  private pointerHeld = false;
  /** Mirrors the server's own "has the artist already placed their one stroke this
   * turn" flag for "One Line Man" - tracked locally too so the artist's own
   * optimistic view predicts the exact same auto-clear the server will generate,
   * instead of only finding out about it later via a relayed event (which would
   * arrive out of order relative to what's already been drawn locally). */
  private hasDrawnFirstStroke = false;
  /** The Line tool, deferred-commit path: active whenever "Tremble" and/or
   * "Claustrophobia" apply to it. While held down, `lineTrueAnchor`/
   * `lineTrueCurrent` track the true (un-jittered, un-clipped) pointer
   * positions; `lineTimer` (Tremble only - claustrophobia-only re-renders once
   * per pointermove instead) re-jitters and re-renders the preview on its own
   * schedule so the line keeps shaking even if the artist's hand holds still.
   * `lineCommittedAnchor`/`lineCommittedEnd` is the last computed (jittered
   * then safe-zone-clipped) pair - what actually gets emitted at pointerup.
   * Only ever non-null between a line-tool pointerdown and its pointerup. */
  private lineTrueAnchor: Point | null = null;
  private lineTrueCurrent: Point | null = null;
  private lineCommittedAnchor: Point | null = null;
  private lineCommittedEnd: Point | null = null;
  private lineTimer: ReturnType<typeof setInterval> | null = null;
  private pendingLineColor: string | null = null;

  constructor(
    private canvas: HTMLCanvasElement,
    private renderer: CanvasRenderer,
    private onEmit: (event: DrawingClientEvent) => void,
    private oneLineManActive = false,
    private trembleLevel: 0 | 1 | 2 | 3 = 0,
    private claustrophobiaLevel: 0 | 1 | 2 = 0,
    private confusionLevel: 0 | 1 | 2 = 0
  ) {
    this.canvas.style.touchAction = "none";
    this.canvas.addEventListener("pointerdown", this.handlePointerDown);
    this.canvas.addEventListener("pointermove", this.handlePointerMove);
    window.addEventListener("pointerup", this.handlePointerUp);
    this.canvas.addEventListener("contextmenu", (e) => e.preventDefault());
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    this.canvas.style.cursor = enabled ? "crosshair" : "not-allowed";
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  setTool(tool: ActiveTool): void {
    this.tool = tool;
  }

  getTool(): ActiveTool {
    return this.tool;
  }

  setColor(color: string): void {
    this.color = color;
  }

  setBrushSize(size: number): void {
    this.brushSize = Math.max(1, Math.min(60, size));
  }

  /** Applies a drawing command to the artist's own view and sends it to the server. */
  private commit(event: DrawingClientEvent): void {
    const rounded = roundEventPoints(event);
    this.renderer.applyLocalEvent(rounded);
    this.onEmit(rounded);
  }

  undo(): void {
    const event: DrawingClientEvent = { type: "undo" };
    this.commit(event);
  }

  redo(): void {
    const event: DrawingClientEvent = { type: "redo" };
    this.commit(event);
  }

  clear(): void {
    const event: DrawingClientEvent = { type: "clear", id: randomId() };
    this.commit(event);
  }

  private toCanvasPoint(e: PointerEvent): Point {
    const rect = this.canvas.getBoundingClientRect();
    const scaleX = CANVAS_WIDTH / rect.width;
    const scaleY = CANVAS_HEIGHT / rect.height;
    return {
      x: Math.max(0, Math.min(CANVAS_WIDTH, (e.clientX - rect.left) * scaleX)),
      y: Math.max(0, Math.min(CANVAS_HEIGHT, (e.clientY - rect.top) * scaleY)),
    };
  }

  private handlePointerDown = (e: PointerEvent): void => {
    if (!this.enabled) return;
    e.preventDefault();
    this.canvas.setPointerCapture(e.pointerId);
    this.pointerHeld = true;
    const point = this.toCanvasPoint(e);

    if (this.tool === "bucket") {
      // "Tremble": the click itself lands a little off from the true cursor
      // position, exactly like a shaky hand missing its mark.
      const fillPoint = this.trembleLevel > 0 ? jitterPoint(point, TREMBLE_CONFIG[this.trembleLevel]!.distance) : point;
      // "Claustrophobia": a click inside the forbidden border does nothing at
      // all - no fill, no mark, exactly like the pen leaves no ink there.
      if (this.claustrophobiaLevel > 0 && !isInSafeZone(fillPoint, CLAUSTROPHOBIA_CONFIG[this.claustrophobiaLevel]!)) {
        return;
      }
      const event: DrawingClientEvent = { type: "fill", id: randomId(), point: fillPoint, color: this.color };
      this.commit(event);
      return;
    }

    // "One Line Man": predict the exact auto-clear the server will independently
    // generate for this 2nd+ stroke, so the artist's own view stays in the same
    // item order as everyone else's (and thus undo/redo stay consistent) instead
    // of only finding out about the clear later via a relayed event.
    if (this.oneLineManActive && this.hasDrawnFirstStroke) {
      const clearEvent: DrawingClientEvent = { type: "clear", id: `${ONE_LINE_MAN_CLEAR_ID_PREFIX}${randomId()}` };
      this.renderer.applyLocalEvent(clearEvent);
    }
    this.hasDrawnFirstStroke = true;

    const tool: ToolType = this.tool === "eraser" ? "eraser" : this.tool === "line" ? "line" : "pen";
    const color = this.tool === "eraser" ? "#ffffff" : this.color;

    // Line tool with "Tremble", "Claustrophobia", and/or "Confusion" active: the
    // eventual endpoints keep drifting/need clipping/inverting for as long as
    // the pointer is held, so nothing is committed/emitted here at all - see
    // startLineDeferred/handlePointerUp for how the final (inverted, then
    // jittered, then clipped, as of the moment of release) endpoints become the
    // usual strokeStart/strokePoint/strokeEnd sequence.
    if (this.tool === "line" && (this.trembleLevel > 0 || this.claustrophobiaLevel > 0 || this.confusionLevel > 0)) {
      this.startLineDeferred(point, color);
      return;
    }

    // "Tremble" + the Pen tool: the actual recorded point wiggles around the
    // true cursor position - re-jittered independently for every point (here,
    // and again in handlePointerMove), which is what gives the resulting line
    // its shaky look.
    const startPoint = this.tool === "pen" && this.trembleLevel > 0 ? jitterPoint(point, TREMBLE_CONFIG[this.trembleLevel]!.distance) : point;

    // "Claustrophobia" + the Pen tool: starting inside the forbidden border
    // leaves no mark - handlePointerMove will begin a fresh stroke if/when the
    // drag re-enters the safe zone.
    if (this.tool === "pen" && this.claustrophobiaLevel > 0 && !isInSafeZone(startPoint, CLAUSTROPHOBIA_CONFIG[this.claustrophobiaLevel]!)) {
      this.lastPoint = point;
      return;
    }

    const id = randomId();
    this.currentStrokeId = id;
    this.lastPoint = point;
    this.lineAnchor = point;
    const event: DrawingClientEvent = { type: "strokeStart", id, tool, color, size: this.brushSize, point: startPoint };
    this.commit(event);
  };

  private handlePointerMove = (e: PointerEvent): void => {
    if (!this.enabled) return;
    const point = this.toCanvasPoint(e);

    if (this.lineTrueAnchor) {
      // Always re-render immediately so the preview tracks the pointer in real
      // time, same as the Line tool normally does - the interval started in
      // startLineDeferred() (Tremble only) is an *additional* trigger on top of
      // this, purely so the line keeps shaking even while the pointer holds
      // still between moves; it was never meant to be the only trigger; gating
      // this render on `trembleLevel === 0` left Tremble's preview only
      // refreshing on the interval's schedule (as slow as every 220ms at level
      // 1), well below the rate pointermove actually fires at, which read as
      // the whole line tool lagging behind the cursor while dragging.
      this.lineTrueCurrent = point;
      this.renderLinePreview();
      return;
    }

    if (this.tool === "line") {
      if (!this.currentStrokeId) return;
      if (this.lineAnchor) {
        this.renderer.previewSegment(this.lineAnchor, point, this.color, this.brushSize);
      }
      this.lastPoint = point;
      return;
    }

    if (this.tool !== "pen" && this.tool !== "eraser") return;
    if (!this.pointerHeld) return;
    if (this.lastPoint && distance(this.lastPoint, point) < 2) return;
    this.lastPoint = point;

    // "Confusion" + the Pen tool: movement away from the true down-point
    // (tracked in `lineAnchor`, unaffected by Confusion itself - see
    // handlePointerDown) is mirrored around it, so the drawn line moves the
    // opposite way from the artist's actual hand movement. Applied before
    // Tremble's jitter so the shake perturbs the already-inverted position,
    // not the raw one.
    const confusionPoint =
      this.tool === "pen" && this.confusionLevel > 0 && this.lineAnchor
        ? invertPoint(this.lineAnchor, point, this.confusionLevel as 1 | 2)
        : point;
    const effective =
      this.tool === "pen" && this.trembleLevel > 0
        ? jitterPoint(confusionPoint, TREMBLE_CONFIG[this.trembleLevel]!.distance)
        : confusionPoint;
    const blocked =
      this.tool === "pen" && this.claustrophobiaLevel > 0 && !isInSafeZone(effective, CLAUSTROPHOBIA_CONFIG[this.claustrophobiaLevel]!);

    if (blocked) {
      if (this.currentStrokeId) {
        // "Claustrophobia": leaving the safe zone mid-stroke lifts the pen, so
        // no connecting line is ever drawn through the forbidden border.
        const endEvent: DrawingClientEvent = { type: "strokeEnd", id: this.currentStrokeId };
        this.commit(endEvent);
        this.currentStrokeId = null;
      }
      return;
    }

    if (!this.currentStrokeId) {
      // Re-entering the safe zone after being lifted (or a drag that began
      // inside the forbidden border) starts a brand-new stroke here, rather
      // than a strokePoint that would connect back through the border to
      // wherever the pen last left off.
      const id = randomId();
      this.currentStrokeId = id;
      const startEvent: DrawingClientEvent = { type: "strokeStart", id, tool: "pen", color: this.color, size: this.brushSize, point: effective };
      this.commit(startEvent);
      return;
    }

    const event: DrawingClientEvent = { type: "strokePoint", id: this.currentStrokeId, point: effective };
    this.commit(event);
  };

  private handlePointerUp = (): void => {
    if (!this.enabled) return;
    this.pointerHeld = false;

    if (this.lineTrueAnchor) {
      const final = this.stopLineDeferred();
      if (!final) return;
      const id = randomId();
      const startEvent: DrawingClientEvent = {
        type: "strokeStart",
        id,
        tool: "line",
        color: this.pendingLineColor ?? this.color,
        size: this.brushSize,
        point: final.anchor,
      };
      this.commit(startEvent);
      const pointEvent: DrawingClientEvent = { type: "strokePoint", id, point: final.end };
      this.commit(pointEvent);
      const endEvent: DrawingClientEvent = { type: "strokeEnd", id };
      this.commit(endEvent);
      return;
    }

    if (!this.currentStrokeId) return;
    const id = this.currentStrokeId;

    if (this.tool === "line" && this.lastPoint) {
      const pointEvent: DrawingClientEvent = { type: "strokePoint", id, point: this.lastPoint };
      this.commit(pointEvent);
    }

    const endEvent: DrawingClientEvent = { type: "strokeEnd", id };
    this.commit(endEvent);

    this.currentStrokeId = null;
    this.lastPoint = null;
    this.lineAnchor = null;
  };

  private startLineDeferred(anchor: Point, color: string): void {
    this.lineTrueAnchor = anchor;
    this.lineTrueCurrent = anchor;
    this.pendingLineColor = color;
    this.renderLinePreview();
    if (this.trembleLevel > 0) {
      const { intervalMs } = TREMBLE_CONFIG[this.trembleLevel]!;
      this.lineTimer = setInterval(() => this.renderLinePreview(), intervalMs);
    }
  }

  private renderLinePreview(): void {
    if (!this.lineTrueAnchor || !this.lineTrueCurrent) return;
    if (!this.canvas.isConnected) {
      // The canvas this editor was built for has been torn down (e.g. the turn
      // ended mid-drag, before a pointerup ever arrived) - stop rendering
      // instead of drawing onto a detached canvas forever.
      this.stopLineDeferred();
      return;
    }
    // "Confusion": only the moving end mirrors around the true anchor - the
    // anchor itself (where the line started) always stays put, exactly like the
    // Pen tool's down-point.
    const confusionCurrent =
      this.confusionLevel > 0
        ? invertPoint(this.lineTrueAnchor, this.lineTrueCurrent, this.confusionLevel as 1 | 2)
        : this.lineTrueCurrent;
    const jitteredAnchor =
      this.trembleLevel > 0 ? jitterPoint(this.lineTrueAnchor, TREMBLE_CONFIG[this.trembleLevel]!.distance) : this.lineTrueAnchor;
    const jitteredEnd =
      this.trembleLevel > 0 ? jitterPoint(confusionCurrent, TREMBLE_CONFIG[this.trembleLevel]!.distance) : confusionCurrent;
    const clipped =
      this.claustrophobiaLevel > 0
        ? clipSegmentToZone(jitteredAnchor, jitteredEnd, CLAUSTROPHOBIA_CONFIG[this.claustrophobiaLevel]!)
        : { a: jitteredAnchor, b: jitteredEnd };

    this.lineCommittedAnchor = clipped?.a ?? null;
    this.lineCommittedEnd = clipped?.b ?? null;

    if (clipped) {
      this.renderer.previewSegment(clipped.a, clipped.b, this.pendingLineColor ?? this.color, this.brushSize);
    } else {
      // Entirely clipped away by Claustrophobia - nothing to preview.
      this.renderer.repaintCommitted();
    }
  }

  private stopLineDeferred(): { anchor: Point; end: Point } | null {
    if (this.lineTimer) {
      clearInterval(this.lineTimer);
      this.lineTimer = null;
    }
    const result =
      this.lineCommittedAnchor && this.lineCommittedEnd
        ? { anchor: this.lineCommittedAnchor, end: this.lineCommittedEnd }
        : null;
    this.lineTrueAnchor = null;
    this.lineTrueCurrent = null;
    this.lineCommittedAnchor = null;
    this.lineCommittedEnd = null;
    this.pendingLineColor = null;
    return result;
  }
}
